import { supabasePublic } from '@/lib/supabase/publicClient';

export interface LandRate {
  id: string;
  locality: string;
  rate: number;
  landType: 'Registered' | 'Unregistered';
  source?: string | null;
  lat?: number | null;
  lng?: number | null;
  createdAt: string;
  downvoteCount: number;
}

export interface CreateLandRateInput {
  locality: string;
  rate: number;
  landType: 'Registered' | 'Unregistered';
  source?: string;
  lat?: number | null;
  lng?: number | null;
}

function rowToLandRate(row: any): LandRate {
  return {
    id: row.id,
    locality: row.locality,
    rate: row.rate,
    landType: row.land_type as LandRate['landType'],
    source: row.source,
    lat: row.lat,
    lng: row.lng,
    createdAt: row.created_at,
    downvoteCount: row.downvote_count ?? 0,
  };
}

export const landRatesService = {
  async getAll(): Promise<LandRate[]> {
    try {
      const PAGE_SIZE = 1000;
      let allRows: any[] = [];
      let from = 0;

      while (true) {
        const { data, error } = await supabasePublic
          .from('land_rates')
          .select('*')
          .order('created_at', { ascending: false })
          .range(from, from + PAGE_SIZE - 1);

        if (error) {
          console.error('Error fetching land rates:', error.message);
          break;
        }

        if (!data || data.length === 0) break;

        allRows = allRows.concat(data);

        if (data.length < PAGE_SIZE) break; // last page
        from += PAGE_SIZE;
      }

      return allRows.map(rowToLandRate);
    } catch (err: any) {
      console.error('landRatesService.getAll failed:', err.message);
      return [];
    }
  },

  async create(input: CreateLandRateInput): Promise<LandRate | null> {
    try {
      const { data, error } = await supabasePublic
        .from('land_rates')
        .insert({
          locality: input.locality,
          rate: input.rate,
          land_type: input.landType,
          source: input.source || null,
          lat: input.lat ?? null,
          lng: input.lng ?? null,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating land rate:', error.message);
        return null;
      }
      return rowToLandRate(data);
    } catch (err: any) {
      console.error('landRatesService.create failed:', err.message);
      return null;
    }
  },

  subscribeToChanges(callback: (rate: LandRate, eventType: 'INSERT' | 'UPDATE' | 'DELETE') => void) {
    const channel = supabasePublic
      .channel('land_rates_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'land_rates' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            callback(rowToLandRate(payload.new), 'INSERT');
          } else if (payload.eventType === 'UPDATE') {
            callback(rowToLandRate(payload.new), 'UPDATE');
          } else if (payload.eventType === 'DELETE') {
            callback(rowToLandRate(payload.old), 'DELETE');
          }
        }
      )
      .subscribe();

    return () => {
      supabasePublic.removeChannel(channel);
    };
  },

  async downvote(landRateId: string): Promise<{ downvoteCount: number } | null> {
    try {
      // Insert downvote record
      const { error: insertError } = await supabasePublic
        .from('downvotes')
        .insert({ land_rate_id: landRateId });

      if (insertError) {
        console.error('Error inserting downvote:', insertError.message);
        return null;
      }

      // Increment counter via RPC
      const { error: rpcError } = await supabasePublic.rpc('increment_downvote_count', {
        rate_id: landRateId,
      });

      if (rpcError) {
        console.error('Error incrementing downvote count:', rpcError.message);
        return null;
      }

      // Fetch updated count
      const { data, error: fetchError } = await supabasePublic
        .from('land_rates')
        .select('downvote_count')
        .eq('id', landRateId)
        .single();

      if (fetchError || !data) {
        console.error('Error fetching updated downvote count:', fetchError?.message);
        return null;
      }

      return { downvoteCount: data.downvote_count ?? 0 };
    } catch (err: any) {
      console.error('landRatesService.downvote failed:', err.message);
      return null;
    }
  },
};
